/**
 * O que a aba **Plantão** de `Manutenção › Indicadores` usa e não é componente (régua da casa: ajudante puro mora
 * em `.ts`). FECHAMENTO-DO-PLANTAO (06/10/2026).
 *
 * 🔴 Os códigos que o servidor e a tela comparam (situação, empresa, motivo, modo) vêm de
 * `src/lib/manutencao/plantao-tipos.ts` — aqui só se LÊ, nunca se reescreve um literal.
 */
import { ApiError } from '@/lib/api';
import { formatarData, mesPorExtenso } from '@/lib/format-date';
import { horaDoAuvo } from '@/lib/manutencao/hora-do-auvo';
import { textoDoPreco } from '@/lib/manutencao/plantao-texto';
import type {
  CartoesDoPlantao,
  LinhaDoPlantao,
  MotivoDeConferencia,
  Mostrar,
  NaoCobrados,
  RegraDePreco,
  RespostaDoPlantao,
  TipoDePdf,
  ValorDoMes,
} from '@/lib/manutencao/plantao-tipos';

const MESES_CURTOS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** `2026-09` → `set`. */
export function mesCurto(mes: string): string {
  const m = /^\d{4}-(\d{2})$/.exec(mes);
  const i = m ? Number(m[1]) - 1 : -1;
  return i >= 0 && i < 12 ? MESES_CURTOS[i] : mes;
}

/** `2026-09` → `setembro de 2026` (sem `Date`: o mês não pode escorregar de fuso). */
export function mesLongo(mes: string): string {
  return mesPorExtenso(mes) ?? mes;
}

/** `2026-09` → `setembro` (o título da tabela: "Atendimentos de setembro"). */
export function nomeDoMes(mes: string): string {
  return mesLongo(mes).replace(/ de \d{4}$/, '');
}

/** Minutos como "1:05" — o "Tempo" da tabela. */
export function horaMinuto(min: number | null | undefined): string {
  if (min === null || min === undefined || !Number.isFinite(min)) return '—';
  const t = Math.max(0, Math.round(min));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
}

/**
 * "03/09 23:10" — a hora de PAREDE do Auvo (a data do Auvo quando não há horário de entrada).
 * 🔴 V1 (adendo unificado): a hora do Auvo NUNCA passa por `Date`/formatador de fuso — ele lia o texto sem fuso como
 * UTC e tirava 3 horas (12:13 no Auvo e no PDF virava 09:13 aqui). Quem formata é `horaDoAuvo`.
 */
export function dataEHora(l: Pick<LinhaDoPlantao, 'entradaIso' | 'data'>): string {
  const entrada = horaDoAuvo(l.entradaIso);
  if (entrada.curta !== '—') return entrada.curta;
  return horaDoAuvo(l.data).diaEMes;
}

/** "02/10" — o dia em que alguém mudou a situação (instante NOSSO, em UTC: este passa pelo formatador de fuso). */
export function diaEMesDe(iso: string | null | undefined): string {
  if (!iso) return '';
  return formatarData(iso, { day: '2-digit', month: '2-digit' });
}

// O texto da regra mora num lugar só (o servidor também o usa).
export { textoDoPreco };

/**
 * "250", "250,50", "1.250,50", "R$ 250" → centavos. `null` quando não é um valor (vazio, letra, negativo).
 */
export function centavosDoTexto(texto: string): number | null {
  const limpo = texto.replace(/R\$/gi, '').replace(/\s/g, '');
  if (!limpo) return null;
  const normal = limpo.includes(',') ? limpo.replace(/\./g, '').replace(',', '.') : limpo;
  if (!/^\d+(\.\d{1,2})?$/.test(normal)) return null;
  const n = Number(normal);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
}

/**
 * Centavos como R$ sem as casas: "R$ 2.360", "R$ 0" (V6, o valor escrito embaixo de cada mês do gráfico; a
 * coluna é estreita e o centavo não ajuda a comparar meses). O valor exato continua no balão da barra.
 */
export function reaisInteiros(centavos: number): string {
  return Math.round(centavos / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

/** Centavos como texto de campo ("250,00") — para preencher um campo de valor. */
export function textoDoCentavos(c: number | null | undefined): string {
  if (c === null || c === undefined) return '';
  return (c / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * As ações que o quadro "Precisa da sua conferência" oferece para cada motivo (além de "Dispensar").
 * `lancar` (29/10/2026): aplica a tabela de plantão sem contrato num clique, sem motivo digitado.
 */
export type AcaoDeConferencia = 'tempo' | 'valor' | 'regra' | 'lancar';

export function acoesDoMotivo(motivo: MotivoDeConferencia, temRegras: boolean, tabelaSemContrato: RegraDePreco | null = null): AcaoDeConferencia[] {
  if (motivo === 'sem_tempo') return ['tempo'];
  if (motivo === 'regras_diferentes') return temRegras ? ['regra'] : ['valor'];
  // 29/10 (Matheus): cliente sem contrato espera o clique em "Lançar", que aplica a tabela de Configurações. Sem a
  // tabela cadastrada, "só o aviso": nenhum botão de valor (fica o Dispensar), e a frase diz onde cadastrar.
  if (motivo === 'sem_contrato_ativo') return tabelaSemContrato ? ['lancar'] : [];
  // `franquias_diferentes` (D1): não há regra de preço a escolher, só o valor a lançar (ou dispensar).
  // #515 `avulsa_sem_valor`: lançar o valor (ou dispensar), por decisão do Matheus.
  return ['valor'];
}

/**
 * A frase extra embaixo do item da conferência: o conserto que mora FORA da aba (o valor por hora da avulsa e a tabela
 * de plantão sem contrato ficam em Configurações e só um administrador os muda), ou o que o "Lançar" vai aplicar.
 * `null` = sem frase.
 */
export function dicaDoMotivo(motivo: MotivoDeConferencia, tabelaSemContrato: RegraDePreco | null = null): string | null {
  if (motivo === 'avulsa_sem_valor') {
    return 'Para que as próximas avulsas sejam cobradas sozinhas, um administrador cadastra o valor por hora em Configurações › Catálogos de contrato › Plantão.';
  }
  if (motivo === 'sem_contrato_ativo') {
    return tabelaSemContrato
      ? `"Lançar" cobra pela tabela de plantão sem contrato: ${textoDoPreco(tabelaSemContrato)}.`
      : 'Para cobrar, um administrador cadastra a tabela de plantão sem contrato em Configurações › Catálogos de contrato › Plantão.';
  }
  return null;
}

/** As corretivas cobradas na aba Plantão (P10 e #515): a mesma tabela, o mesmo controle, rótulos próprios. */
export function ehCorretiva(l: Pick<LinhaDoPlantao, 'origem'>): boolean {
  return l.origem === 'corretiva_excedente' || l.origem === 'corretiva_avulsa';
}

export const ROTULO_DA_ACAO: Record<AcaoDeConferencia | 'dispensar', string> = {
  tempo: 'Informar tempo',
  valor: 'Lançar valor',
  regra: 'Escolher regra',
  lancar: 'Lançar',
  dispensar: 'Dispensar',
};

export const OPCOES_DE_MOSTRAR: Array<{ valor: Mostrar; rotulo: string }> = [
  { valor: 'todos', rotulo: 'Todos' },
  { valor: 'a_faturar', rotulo: 'A faturar' },
  { valor: 'faturado', rotulo: 'Faturado' },
  { valor: 'nao_cobrar', rotulo: 'Não cobrar' },
];

export function mostrarValido(v: string | null | undefined): Mostrar {
  return OPCOES_DE_MOSTRAR.some((o) => o.valor === v) ? (v as Mostrar) : 'todos';
}

/**
 * A frase do servidor num erro de escrita (`{ mensagem }`), ou uma de gente. Código cru nunca vai para a tela.
 */
export function fraseDoErro(e: unknown, padrao = 'Não foi possível salvar agora. Tente de novo em alguns instantes.'): string {
  if (e instanceof ApiError) {
    if (e.status === 403) return 'Você não tem permissão para esta ação. Peça a um administrador.';
    const r = e.response as { mensagem?: unknown } | undefined;
    if (r && typeof r.mensagem === 'string' && r.mensagem.trim()) return r.mensagem;
  }
  return padrao;
}

// ─── Normalização defensiva da resposta ──────────────────────────────────────
// Servidor velho durante o deploy (ou campo que ainda não existe) não pode derrubar a aba: o que faltar vira
// vazio ou zero.

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const lista = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

function cartoes(c: Partial<CartoesDoPlantao> | undefined): CartoesDoPlantao {
  const x = c ?? {};
  return {
    totalCentavos: num(x.totalCentavos),
    igmCentavos: num(x.igmCentavos),
    routeCentavos: num(x.routeCentavos),
    atendimentos: num(x.atendimentos),
    cobraveis: num(x.cobraveis),
    naoCobrados: num(x.naoCobrados),
    naoCobradosContrato: num(x.naoCobradosContrato),
    naoCobradosFranquia: num(x.naoCobradosFranquia),
    naoCobradosDecisao: num(x.naoCobradosDecisao),
    emConferencia: num(x.emConferencia),
    horasCobradas: num(x.horasCobradas),
    minutosNoLocal: num(x.minutosNoLocal),
    faturadoCentavos: num(x.faturadoCentavos),
    faltaFaturarCentavos: num(x.faltaFaturarCentavos),
  };
}

function naoCobrados(n: Partial<NaoCobrados> | undefined): NaoCobrados {
  const x = n ?? {};
  return {
    quantidade: num(x.quantidade),
    minutos: num(x.minutos),
    contrato: { quantidade: num(x.contrato?.quantidade), minutos: num(x.contrato?.minutos) },
    franquia: { quantidade: num(x.franquia?.quantidade), minutos: num(x.franquia?.minutos) },
    decisao: { quantidade: num(x.decisao?.quantidade), minutos: num(x.decisao?.minutos) },
    porMotivo: lista(x.porMotivo),
    clientes: lista(x.clientes),
    limite: num(x.limite),
  };
}

export function normalizarPlantao(cru: unknown, mesPedido: string | null): RespostaDoPlantao {
  const r = (cru && typeof cru === 'object' ? cru : {}) as Partial<RespostaDoPlantao>;
  const mes = typeof r.mes === 'string' && r.mes ? r.mes : (mesPedido ?? '');
  const filtros = r.filtros ?? { mes, empresa: null, clienteId: null, tecnico: null, mostrar: 'todos' as Mostrar };
  return {
    mes,
    mesDeHoje: typeof r.mesDeHoje === 'string' ? r.mesDeHoje : '',
    filtros: { ...filtros, mostrar: mostrarValido(filtros.mostrar) },
    cartoes: cartoes(r.cartoes),
    conferencia: lista(r.conferencia),
    linhas: lista(r.linhas),
    porTecnico: lista(r.porTecnico),
    naoCobrados: naoCobrados(r.naoCobrados),
    porMes: { desde: r.porMes?.desde ?? null, meses: lista<ValorDoMes>(r.porMes?.meses).map(valorDoMes) },
    clientes: lista(r.clientes),
    tecnicos: lista(r.tecnicos),
    motivos: lista(r.motivos),
    tabelaRoute: r.tabelaRoute ?? null,
    tabelaSemContrato: r.tabelaSemContrato ?? null,
    podeEnviarResumo: r.podeEnviarResumo === true,
    podeCorrigir: r.podeCorrigir === true,
    excluidos: lista(r.excluidos),
    pendencias: { aFaturar: num(r.pendencias?.aFaturar), emConferencia: num(r.pendencias?.emConferencia) },
    excedentes: excedentes(r.excedentes),
    avulsas: excedentes(r.avulsas),
  };
}

function valorDoMes(m: Partial<ValorDoMes>): ValorDoMes {
  return {
    mes: typeof m.mes === 'string' ? m.mes : '',
    igmCentavos: num(m.igmCentavos),
    routeCentavos: num(m.routeCentavos),
    emAndamento: m.emAndamento === true,
    naoCobradosContrato: num(m.naoCobradosContrato),
    naoCobradosFranquia: num(m.naoCobradosFranquia),
    naoCobradosDecisao: num(m.naoCobradosDecisao),
  };
}

/**
 * P10 (e #515, as avulsas, que têm a MESMA forma): ausente (servidor velho) continua ausente — a tela não desenha
 * seção para o que não existe.
 */
function excedentes(e: RespostaDoPlantao['excedentes'] | undefined): RespostaDoPlantao['excedentes'] {
  if (!e || typeof e !== 'object') return undefined;
  return {
    linhas: lista(e.linhas),
    conferencia: lista(e.conferencia),
    totalCentavos: num(e.totalCentavos),
    faturadoCentavos: num(e.faturadoCentavos),
    faltaFaturarCentavos: num(e.faltaFaturarCentavos),
  };
}

/**
 * A seção das corretivas além da franquia (e a das avulsas, #515) só existe quando há o que mostrar ("zero não é um
 * lugar para ir").
 */
export function temExcedentes(e: RespostaDoPlantao['excedentes'] | RespostaDoPlantao['avulsas']): boolean {
  return !!e && (e.linhas.length > 0 || e.conferencia.length > 0);
}

/** "Pelo contrato" · "Franquia do contrato" · o motivo de quem decidiu. `null` fora do "Não cobrar". */
export function textoDoNaoCobrar(l: Pick<LinhaDoPlantao, 'situacao' | 'naoCobrarTipo' | 'motivo'>): string | null {
  if (l.situacao !== 'nao_cobrar') return null;
  // O automático pelo contrato traz o nome na linha: "Pelo contrato" ou (29/10) "Contrato em implantação".
  if (l.naoCobrarTipo === 'contrato') return l.motivo?.nome ?? 'Pelo contrato';
  if (l.naoCobrarTipo === 'franquia') return 'Franquia do contrato';
  return [l.motivo?.nome, l.motivo?.texto].filter(Boolean).join(': ') || null;
}

/** A frase do "Enviar resumo do mês" quando ainda há pendência — a parte que é zero não aparece. */
export function fraseDasPendencias(aFaturar: number, emConferencia: number): string | null {
  const partes: string[] = [];
  if (aFaturar > 0) partes.push(`${aFaturar} a faturar`);
  if (emConferencia > 0) partes.push(`${emConferencia} em conferência`);
  if (partes.length === 0) return null;
  return `Ainda há ${partes.join(' e ')}. Enviar assim mesmo?`;
}

/**
 * O endereço do PDF. Os ids dos clientes vão separados por vírgula SEM codificar a vírgula — o servidor separa
 * por ela (`server/routes/manutencao-plantao.ts`, GET /pdf).
 */
export function enderecoDoPdf(o: {
  tipo: TipoDePdf;
  mes: string;
  mostrar: Mostrar;
  clientes?: string[];
  tecnico?: string | null;
}): string {
  let url = `/api/v1/manutencao/plantao/pdf?tipo=${o.tipo}&mes=${encodeURIComponent(o.mes)}&mostrar=${o.mostrar}`;
  if (o.tipo === 'cliente') url += `&clientes=${(o.clientes ?? []).map((id) => encodeURIComponent(id)).join(',')}`;
  if (o.tipo === 'tecnico' && o.tecnico) url += `&tecnico=${encodeURIComponent(o.tecnico)}`;
  return url;
}

/** Os parâmetros da URL que a aba lê e grava. */
export interface FiltrosDoPlantao {
  mes: string | null;
  empresa: 'IGM' | 'ROUTE' | null;
  cliente: string | null;
  tecnico: string | null;
  mostrar: Mostrar;
}

/** A query de leitura (GET da aba e da planilha): só o que tem valor. */
export function queryDoPlantao(f: FiltrosDoPlantao): string {
  const p = new URLSearchParams();
  if (f.mes) p.set('mes', f.mes);
  if (f.empresa) p.set('empresa', f.empresa);
  if (f.cliente) p.set('cliente', f.cliente);
  if (f.tecnico) p.set('tecnico', f.tecnico);
  if (f.mostrar !== 'todos') p.set('mostrar', f.mostrar);
  const s = p.toString();
  return s ? `?${s}` : '';
}
